// Internal Products page card strip — the product-level counterpart to the
// Brands page's BrandIntelFooter. The card itself already lists every PDP
// URL with its latest score, report and Run PDP Analysis icon (per brand),
// so this strip carries the Consumer Voice and AI Presence lines: score,
// view / download of the last analysis, and a run icon each.
//
// Brand scoping: every Consumer Voice audit is stamped with a
// `subject_brand_id`, so one product can carry a separate CV lineage per
// brand. The server resolves the product's manufacturer (its non-network
// brand) as `intel_brand_id` and scores the card against it; we send that
// same id back on every run/view call, so what a card shows and what its
// button starts can never belong to different brands. A product with no
// manufacturer brand has nothing to stamp — the CV controls go disabled
// rather than firing a request that would 400 on `BrandIdRequired`.
import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { FileDownloadOutlined, VisibilityOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  productAiPresenceItemEndpoint,
  productConsumerVoiceItemEndpoint,
} from '../../../api/endpoints';
import { AipScoreChip } from '../../../components/AiPresence/AipScoreChip';
import { AiPresenceReportDialog } from '../../../components/AiPresence/AiPresenceReportDialog';
import { AiPresenceRunDialog } from '../../../components/AiPresence/AiPresenceRunDialog';
import { downloadAiPresencePdf } from '../../../components/AiPresence/aiPresenceFiles';
import { AipSubscores } from '../../../types/aiPresence';
import { CvScoreChip } from '../../../components/ConsumerVoice/CvScoreChip';
import { ConsumerVoiceReportDialog } from '../../../components/ConsumerVoice/ConsumerVoiceReportDialog';
import { ConsumerVoiceRunDialog } from '../../../components/ConsumerVoice/ConsumerVoiceRunDialog';
import { downloadConsumerVoicePdf } from '../../../components/ConsumerVoice/consumerVoiceFiles';
import { IconRefresh } from '../../../utils/icons';
import { RunningBadge } from '../../../components/RunningBadge';
import { useStrategyFromAnalysis } from '../../../components/ProposalFlow/useStrategyFromAnalysis';

export interface ProductIntelRow {
  desireid: number;
  title?: string | null;
  brands?: Array<{ id: number; name: string }> | null;
  pdp_score?: number | null;
  latest_report?: {
    score_id: number;
    pdp_url_id: number;
    pdp_url: string;
    scored_at: string;
  } | null;
  pdp_report_running?: boolean;
  cv_score?: number | null;
  cv_latest?: { id: number; scored_at: string } | null;
  cv_running?: boolean;
  aip_score?: number | null;
  aip_subscores?: AipSubscores | null;
  aip_latest?: { id: number; scored_at: string } | null;
  aip_running?: boolean;
  // Resolved server-side (pdpUrlCounts.attachManufacturerBrand).
  intel_brand_id?: number | null;
  intel_brand_name?: string | null;
}

const icon = 'w-5 h-5 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0';

export function ProductIntelFooter({
  product,
  onChanged,
}: {
  product: ProductIntelRow;
  onChanged?: () => void;
}) {
  const [viewingCv, setViewingCv] = useState<number | null>(null);
  const createStrategy = useStrategyFromAnalysis();

  const brandId = product.intel_brand_id ?? null;
  const cvEndpoint = (auditId: number) =>
    productConsumerVoiceItemEndpoint(product.desireid, auditId, brandId);

  // Asks for subreddit hints first (ConsumerVoiceRunDialog), then starts.
  const [runOpen, setRunOpen] = useState(false);
  const runCv = () => {
    if (!brandId) return;
    setRunOpen(true);
  };

  const cvTitle = (): string => {
    if (!brandId) return 'No manufacturer brand on this product yet — set its brand in Settings first.';
    if (product.cv_running) return 'A Consumer Voice analysis is running for this product — the score updates when it finishes.';
    const suffix = product.intel_brand_name ? ` for ${product.intel_brand_name}` : '';
    return `Run CV Analysis: read what shoppers say about this product on Reddit${suffix}`;
  };

  // One line, mirroring the card's PDP URL rows: score chip, then view /
  // download of the last analysis (only once there is one), then the run
  // icon — which stays even with no analysis yet, since that is exactly
  // when you want it.
  const latest = product.cv_latest;

  const [viewingAip, setViewingAip] = useState<number | null>(null);
  const [aipRunOpen, setAipRunOpen] = useState(false);
  const aipEndpoint = (auditId: number) => productAiPresenceItemEndpoint(product.desireid, auditId, brandId);
  const aipLatest = product.aip_latest;
  const aipTitle = (): string => {
    if (!brandId) return 'No manufacturer brand on this product yet — set its brand in Settings first.';
    if (product.aip_running) return 'An AI Presence analysis is running for this product — the score updates when it finishes.';
    const suffix = product.intel_brand_name ? ` for ${product.intel_brand_name}` : '';
    return `Run AI Presence: how often Claude, Gemini and OpenAI recommend this product${suffix}`;
  };
  return (
    <div className="flex flex-col gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1 text-[11px] text-muted min-w-0">
        <span className="shrink-0">Consumer Voice</span>
        {typeof product.cv_score === 'number' ? (
          <CvScoreChip
            score={product.cv_score}
            title={
              product.intel_brand_name
                ? `Consumer Voice Score for ${product.intel_brand_name}`
                : 'Consumer Voice Score: this product’s latest analysis'
            }
          />
        ) : (
          <span>—</span>
        )}
        {latest && (
          <>
            <Tooltip title={`View latest analysis (${dayjs(latest.scored_at).format('MMM DD, YYYY')})`}>
              <button
                type="button"
                className={icon}
                onClick={() => setViewingCv(latest.id)}
                aria-label="View Consumer Voice analysis"
              >
                <VisibilityOutlined sx={{ fontSize: 14 }} />
              </button>
            </Tooltip>
            <Tooltip title="Download latest analysis (PDF)">
              <button
                type="button"
                className={icon}
                onClick={() => downloadConsumerVoicePdf(cvEndpoint(latest.id))}
                aria-label="Download Consumer Voice analysis"
              >
                <FileDownloadOutlined sx={{ fontSize: 14 }} />
              </button>
            </Tooltip>
          </>
        )}
        {/* In flight: the run icon becomes the same spinner badge the BI
            line on brand cards shows, so a CV run is visibly in progress. */}
        {product.cv_running ? (
          <RunningBadge label="Analyzing" title={cvTitle()} />
        ) : (
          <Tooltip title={cvTitle()}>
            {/* span: MUI Tooltip needs a hoverable child even when disabled */}
            <span className="inline-flex">
              <button
                type="button"
                className={`${icon} disabled:opacity-40 disabled:cursor-not-allowed`}
                onClick={runCv}
                disabled={!brandId}
                aria-label="Run CV Analysis"
              >
                <IconRefresh size={13} />
              </button>
            </span>
          </Tooltip>
        )}
      </div>
      <div className="flex items-center gap-1 text-[11px] text-muted min-w-0">
        <span className="shrink-0">AI Presence</span>
        {typeof product.aip_score === 'number' ? (
          <AipScoreChip score={product.aip_score} subscores={product.aip_subscores} />
        ) : (
          <span>—</span>
        )}
        {aipLatest && (
          <>
            <Tooltip title={`View latest analysis (${dayjs(aipLatest.scored_at).format('MMM DD, YYYY')})`}>
              <button type="button" className={icon} onClick={() => setViewingAip(aipLatest.id)} aria-label="View AI Presence analysis">
                <VisibilityOutlined sx={{ fontSize: 14 }} />
              </button>
            </Tooltip>
            <Tooltip title="Download latest analysis (PDF)">
              <button
                type="button"
                className={icon}
                onClick={() => downloadAiPresencePdf(aipEndpoint(aipLatest.id))}
                aria-label="Download AI Presence analysis"
              >
                <FileDownloadOutlined sx={{ fontSize: 14 }} />
              </button>
            </Tooltip>
          </>
        )}
        {product.aip_running ? (
          <RunningBadge label="Analyzing" title={aipTitle()} />
        ) : (
          <Tooltip title={aipTitle()}>
            <span className="inline-flex">
              <button
                type="button"
                className={`${icon} disabled:opacity-40 disabled:cursor-not-allowed`}
                onClick={() => brandId && setAipRunOpen(true)}
                disabled={!brandId}
                aria-label="Run AI Presence"
              >
                <IconRefresh size={13} />
              </button>
            </span>
          </Tooltip>
        )}
      </div>
      {viewingAip !== null && (
        <AiPresenceReportDialog
          endpoint={aipEndpoint(viewingAip)}
          onClose={() => setViewingAip(null)}
          onCreateStrategy={(audit) => createStrategy('aip', { id: audit.id, desireid: product.desireid }, product.title)}
        />
      )}
      {aipRunOpen && brandId && (
        <AiPresenceRunDialog
          desireid={product.desireid}
          brandId={brandId}
          productTitle={product.title}
          onClose={() => setAipRunOpen(false)}
          onStarted={() => onChanged?.()}
        />
      )}
      {viewingCv !== null && (
        <ConsumerVoiceReportDialog
          endpoint={cvEndpoint(viewingCv)}
          onClose={() => setViewingCv(null)}
          onCreateStrategy={(audit) => createStrategy('cv', { id: audit.id, desireid: product.desireid }, product.title)}
        />
      )}
      {runOpen && brandId && (
        <ConsumerVoiceRunDialog
          desireid={product.desireid}
          brandId={brandId}
          productTitle={product.title}
          onClose={() => setRunOpen(false)}
          onStarted={() => onChanged?.()}
        />
      )}
    </div>
  );
}
